import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { formatCount, formatPkr } from 'utils/currency';

export interface SalesUptimePoint {
  label: string;
  invoiceCount: number;
  revenue: number;
}

interface SalesUptimeChartProps {
  data?: SalesUptimePoint[];
  bucket?: string;
  height?: number;
}

const SalesUptimeChart: React.FC<SalesUptimeChartProps> = ({
  data = [],
  bucket = 'day',
  height = 300,
}) => {
  const bucketHint =
    bucket === 'hour'
      ? 'Sales by hour — darker bars mean more activity'
      : bucket === 'month'
      ? 'Sales by month'
      : 'Sales by day';

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    colors: ['#0284c7', '#10b981'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: bucket === 'hour' ? '70%' : '55%',
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: data.map((d) => d.label),
      labels: {
        rotate: bucket === 'hour' ? -45 : 0,
        style: { fontSize: '11px' },
      },
    },
    yaxis: [
      {
        title: { text: 'Invoices' },
        labels: { formatter: (v) => formatCount(Number(v)) },
      },
      {
        opposite: true,
        title: { text: 'Revenue (PKR)' },
        labels: {
          formatter: (v) => Number(v).toLocaleString('en-PK', { maximumFractionDigits: 0 }),
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val, opts) => {
          if (opts.seriesIndex === 1) return formatPkr(Number(val));
          return formatCount(Number(val));
        },
      },
    },
    legend: { position: 'top', horizontalAlign: 'right' },
    subtitle: {
      text: bucketHint,
      style: { fontSize: '12px', color: '#6b7280' },
    },
  };

  const series = [
    { name: 'Invoices', type: 'column' as const, data: data.map((d) => d.invoiceCount) },
    { name: 'Revenue', type: 'line' as const, data: data.map((d) => d.revenue) },
  ];

  return (
    <ReactApexChart
      options={chartOptions}
      series={series}
      type="line"
      height={height}
      width="100%"
    />
  );
};

export default SalesUptimeChart;
